import { Effect } from 'effect';
import type { Bbox, ViewportLike } from '../types';

/**
 * Convert a ParseBench-style bbox tuple to a normalized {x,y,w,h}.
 *
 * Gemini-3-flash-minimal (and other ParseBench-flavored parsers) emit raw
 * tuples in the order [y_min, x_min, y_max, x_max] on a 0..1000 grid for
 * BOTH axes. Convert to the canonical normalized 0-1 shape.
 */
function normalizeBboxUnsafe(raw: readonly [number, number, number, number]): Bbox {
  const [yMin, xMin, yMax, xMax] = raw;
  return {
    x: xMin / 1000,
    y: yMin / 1000,
    w: (xMax - xMin) / 1000,
    h: (yMax - yMin) / 1000,
  };
}

export function normalizeBboxEffect(
  raw: readonly [number, number, number, number],
): Effect.Effect<Bbox> {
  return Effect.sync(() => normalizeBboxUnsafe(raw));
}

export function normalizeBbox(raw: readonly [number, number, number, number]): Bbox {
  return Effect.runSync(normalizeBboxEffect(raw));
}

/**
 * Coerce a `{x,y,w,h}` bbox to the canonical 0-1 unit space, **auto-detecting a
 * 0-1000 grid**. VLM grounding output (Qwen2.5-VL, Gemini, ParseBench) is native
 * 0-1000; if any coordinate is > 1 the box is assumed to be on that grid and
 * divided down (matching @okrapdf/pdfdom's `normalizeBbox`). Idempotent for
 * already-normalized 0-1 input; clamps each coordinate into [0,1].
 *
 * This is what makes the surface tolerant of raw VLM boxes — pass Qwen-scale
 * coords straight through and they render correctly.
 */
function toUnitBboxUnsafe(b: Bbox): Bbox {
  let { x, y, w, h } = b;
  if (x > 1 || y > 1 || w > 1 || h > 1) {
    x /= 1000;
    y /= 1000;
    w /= 1000;
    h /= 1000;
  }
  const c = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);
  return { x: c(x), y: c(y), w: c(w), h: c(h) };
}

export function toUnitBboxEffect(b: Bbox): Effect.Effect<Bbox> {
  return Effect.sync(() => toUnitBboxUnsafe(b));
}

export function toUnitBbox(b: Bbox): Bbox {
  return Effect.runSync(toUnitBboxEffect(b));
}

/**
 * Map a normalized bbox to canvas pixel space given a pdf.js-style viewport.
 * The returned rect's origin is the top-left of the canvas.
 */
export function scaleToCanvasEffect(
  bbox: Bbox,
  viewport: ViewportLike,
): Effect.Effect<{ x: number; y: number; w: number; h: number }> {
  return Effect.succeed({
    x: bbox.x * viewport.width,
    y: bbox.y * viewport.height,
    w: bbox.w * viewport.width,
    h: bbox.h * viewport.height,
  });
}

export function scaleToCanvas(
  bbox: Bbox,
  viewport: ViewportLike,
): { x: number; y: number; w: number; h: number } {
  return Effect.runSync(scaleToCanvasEffect(bbox, viewport));
}

/**
 * Clip a normalized bbox against the unit rect (0,0,1,1). Returns null if
 * the bbox is fully outside.
 */
function clipToUnitUnsafe(bbox: Bbox): Bbox | null {
  // Short-circuit the common no-clipping case so we don't introduce
  // float-precision drift on the (numerous) bboxes that are fully inside.
  if (bbox.x >= 0 && bbox.y >= 0 && bbox.x + bbox.w <= 1 && bbox.y + bbox.h <= 1) {
    return bbox;
  }
  const x0 = Math.max(0, bbox.x);
  const y0 = Math.max(0, bbox.y);
  const x1 = Math.min(1, bbox.x + bbox.w);
  const y1 = Math.min(1, bbox.y + bbox.h);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function clipToUnitEffect(bbox: Bbox): Effect.Effect<Bbox | null> {
  return Effect.sync(() => clipToUnitUnsafe(bbox));
}

export function clipToUnit(bbox: Bbox): Bbox | null {
  return Effect.runSync(clipToUnitEffect(bbox));
}

/**
 * Numeric area of a normalized bbox.
 */
export function bboxArea(bbox: Bbox): number {
  return Effect.runSync(bboxAreaEffect(bbox));
}

export function bboxAreaEffect(bbox: Bbox): Effect.Effect<number> {
  return Effect.succeed(Math.max(0, bbox.w) * Math.max(0, bbox.h));
}
